/**
 * The Notes tab (#676, a list since #743): pure rules. Every member of the
 * reader's licence adds notes; only a note's author edits or deletes it (the
 * server says so with `can_edit`). The server normalises what it stores (line
 * endings, blank space around), so these rules only decide what the tab says.
 */

import { PROFILE_NOTES_MAX_LENGTH, type ProfileNote } from '@shared/schemas';

import { formatDateTimeFr } from '@/utils/format';

export { PROFILE_NOTES_MAX_LENGTH };

/** What a note whose author was deleted shows in place of a name. */
export const FORMER_USER = 'Former user';

/** Who wrote the note: "Dorine Martin", or `FORMER_USER` once that user is deleted. */
export function noteAuthorName(note: ProfileNote): string {
  const who = note.author;
  return who ? `${who.first_name} ${who.last_name}`.trim() || FORMER_USER : FORMER_USER;
}

/** When the note was written, "15/10/2026 12:00", and whether it was edited since. */
export function noteDateLine(note: ProfileNote): { created: string; edited: boolean } {
  return { created: formatDateTimeFr(note.created_at), edited: note.updated_at !== null };
}

/** The draft as the server would store it. */
function normalise(draft: string): string {
  return draft.replace(/\r\n?/g, '\n').trim();
}

/**
 * Whether a draft may be saved: it must not be blank (a note is deleted, never
 * emptied), fit the cap, and, when editing, differ from the note as stored;
 * compared as the server would store it, so re-adding a trailing space is not a
 * change.
 */
export function canSaveNote(draft: string, saved: string | null): boolean {
  if (draft.length > PROFILE_NOTES_MAX_LENGTH) return false;
  const next = normalise(draft);
  return next !== '' && next !== saved;
}

/**
 * Whether a failed edit or delete answered that the note itself is gone (404
 * "Note not found": deleted meanwhile, e.g. by its author in another tab), as opposed
 * to the profile being out of reach (404 "Profile not found") or any other
 * failure. Only then does the tab drop the note from its list.
 */
export function isNoteGone(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const status = (e as { status?: unknown }).status;
  return status === 404 && e.message === 'Note not found';
}
